import { trainStationCodeMap } from "@/config/trainStationCodeMap"
import { airportCodeMap } from "@/config/airportCodeMap"
import flightPackCode from "@/config/flightPackCode"
import trainPackCode from "@/config/trainPackCode"
import { getPrestTotalPrice, getPriceAccommodationOnly } from "@/logic/pricesAndDiscount"

// Same origin resolution as getWiypData so summary totals match card prices
const resolveOrigin = (state) => {
	const packCode = state.packCode
	if (!packCode) return "NA"

	const rawOrigin = state.flightOrigin ?? state.wiypDefaultFlightOrigin ?? ""

	if (flightPackCode.includes(packCode)) {
		return airportCodeMap.has(rawOrigin)
			? rawOrigin
			: Array.from(airportCodeMap.keys())[0] || "NA"
	}

	if (trainPackCode.includes(packCode)) {
		return trainStationCodeMap.has(rawOrigin)
			? rawOrigin
			: Array.from(trainStationCodeMap.keys())[0] || "NA"
	}

	return "NA"
}

// Same packageId key as PrestVariantDefault / getWiypPrice (wiypCall is stored under this id)
const resolveWiypPackageId = (prestation) => {
	if (prestation.codePackage === "HS") {
		return `${prestation.fusionId ?? prestation.liheId}HS`
	}
	return prestation.prestId
}

export default {
	totals: (state, getters) => {

		const selectedPrestations = getters.selectedPrestations

		const participants = state.participants

		let totalPrice = 0
		let crossedPrice = 0

		if (selectedPrestations.length > 0) {
			const origin = resolveOrigin(state)

			let firstAccommodation = true;
			selectedPrestations.forEach((prestObj) => {
				const packageId = resolveWiypPackageId(prestObj.prestation)
				const wiypData =
					getters.getWiypCallByFamilyCompositionAndOriginAndPackageCodeAndId(
						participants.join("-"),
						origin,
						state.packCode,
						packageId
					) ??
					// Fallbacks for older/virtual-stock keys
					getters.getWiypCallByFamilyCompositionAndOriginAndPackageCodeAndId(
						participants.join("-"),
						origin,
						state.packCode,
						prestObj.prestation.prestId
					) ??
					getters.getWiypCallByFamilyCompositionAndOriginAndPackageCodeAndId(
						participants.join("-"),
						origin,
						state.packCode,
						prestObj.prestation.prestIdFusion
					)

				const wiypPrice = wiypData?.wiypPrices?.find(
					(w) => w.prestId === prestObj.prestation.prestIdSource
				)

				// Align with card display: prefer WIYP selling price when available.
				// Do not require accomPriceWiyp for the selling price (card doesn't).
				// Also clear pricePerLogementWithoutFee so getPrestTotalPrice cannot
				// return a stale quote "without fee" value (~half the package price).
				if (wiypPrice?.sellingPriceWiyp) {
					prestObj.prestation.pricePerLogement = wiypPrice.sellingPriceWiyp
					prestObj.prestation.pricePerPerson =
						wiypPrice.sellingPriceWiyp / participants.length
					delete prestObj.prestation.pricePerLogementWithoutFee

					if (wiypPrice.publicPriceWiyp) {
						prestObj.prestation.crossedPrice = wiypPrice.publicPriceWiyp
						prestObj.prestation.maxPromo = Math.round(
							((wiypPrice.publicPriceWiyp - wiypPrice.sellingPriceWiyp) /
								wiypPrice.publicPriceWiyp) *
								100
						)
					}
					if (wiypPrice.accomPriceWiyp) {
						prestObj.prestation.priceAccommodationOnly = wiypPrice.accomPriceWiyp
					}
				}
				let quantity = prestObj.quantity
				if (firstAccommodation) {
					firstAccommodation = false;
					totalPrice += getPrestTotalPrice(prestObj.prestation);
					crossedPrice += prestObj.prestation.crossedPrice
					quantity -= 1;
				}
				if (prestObj.prestation.priceAccommodationOnly) {
					totalPrice += quantity * getPriceAccommodationOnly(prestObj.prestation);
				} else {
					totalPrice += quantity * getPrestTotalPrice(prestObj.prestation);
				}				
				crossedPrice += quantity * prestObj.prestation.crossedPrice;
			})
		}

		let perPrice = totalPrice / participants.length

		const obj = {}

		obj["totalPrice"] = totalPrice
		obj["perPrice"] = perPrice
		// Because of the wiyp pricing and priceAccommodationOnly, we don't need to calculate the global max promo, except for HS
		let maxPromo = parseInt((1 - totalPrice / crossedPrice) * 100)
		if (state.packCode =='HS' && maxPromo >= 5) obj["crossedPrice"] = crossedPrice
		if (state.packCode =='HS' && maxPromo >= 5) obj["maxPromo"] = maxPromo

		return obj
	}
}
